// src/pages/ExploreMore.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ResultCard from "../components/ResultCard";
import "./ExploreMore.css";

import axios from "axios"; // if you're using axios

// Round 6 (2026-05-05): API_BASE resolution standardized to match aiApi.js +
// siteFeedbackApi.js. Was previously `{API_BASE1}` (template-literal typo —
// missing `$`) which made local dev fetch the React dev server's index.html
// instead of the backend, surfacing as "Could not parse API response". In
// production, Vercel sets REACT_APP_API_BASE so the fallback never fires.
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5050";

// 2026-05-06 (Tier 2.3): Replaced Unsplash with backend Google Static Street
// View proxy. Unsplash flow was broken anyway (REACT_APP_UNSPLASH_KEY never
// set in prod) and returned generic stock photos that didn't match the POI.
// Street View gives you the actual storefront/park, geocoded server-side.
// Backend route (placesRoute.js) does a free metadata pre-check before any
// billable image call and returns 404 when no Street View imagery exists —
// `onError` falls through to heroImage.
//
// 2026-05-06 fix: include `neighborhood` in the geocoder query. Without it,
// Google's geocoder maps chain-store queries like "H-E-B Grocery, Austin" to
// the city centroid (there are 30+ H-E-Bs in Austin — geocoder picks a
// default), so Street View returns the same panorama for many POIs. Adding
// the neighborhood (e.g. "South Congress") narrows the geocode to the actual
// branch we mean.
const streetViewUrl = (title, neighborhood, city, lat, lng, size = "400x250") => {
  const params = new URLSearchParams({ size });
  if (lat != null && lng != null) {
    params.set("lat", String(lat));
    params.set("lng", String(lng));
  } else {
    // Strip parenthetical aliases ("(SoCo)") so the geocoder gets a clean
    // landmark string. e.g. "South Congress (SoCo)" → "South Congress".
    const cleanNeighborhood = (neighborhood || "")
      .replace(/\s*\([^)]*\)\s*/g, " ")
      .trim();
    const parts = [title, cleanNeighborhood, city].filter(Boolean);
    params.set("q", parts.join(", "));
  }
  return `${API_BASE}/api/places/streetview?${params.toString()}`;
};

// simple debounce
function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const CATS = ["Groceries", "Food & Drinks", "Schools", "Parks", "Activities"];

// build a Google Maps search URL for "{place} {city}"
const mapsUrl = (place, city, lat, lng) =>
  lat != null && lng != null
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${place} ${city}`)}`;

// 2026-05-06: fetchUnsplashImage removed — replaced by streetViewUrl above.
// Image fetching is now synchronous URL building; the actual GET is fired
// by the <img> tag, which handles loading + error fallback natively.

/**
 * ExploreMore — POI explorer for a neighborhood.
 *
 * Supports two render modes (Round 6, 2026-05-05 LG feedback):
 *
 * 1) Standalone page (default) — accessed via /areas/:slug/explore deep link.
 *    Reads context from useLocation().state and useParams.slug.
 * 2) Embedded mode — used inside AIChat's right panel. Parent passes
 *    `embedded={true}` plus `propsItem`, `propsCity`, `propsHeroImage`,
 *    `onClose`. The internal topbar is hidden; back action invokes onClose().
 *
 * 2026-05-05 cleanup: file was previously 1700+ lines containing 6 stacked
 * legacy versions of this component (alternating block-comments and live
 * code). Truncated to just the active version. Same pattern as the prior
 * Home.jsx truncation (5,987 → 870 lines).
 */
export default function ExploreMore({
  embedded = false,
  propsItem = null,
  propsCity = null,
  propsHeroImage = null,
  onClose = null,
} = {}) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { slug } = useParams();

  // Resolve data source: embedded mode prefers props, standalone uses router state.
  const areaTitle = embedded
    ? (propsItem?.title || propsItem?.name || "Area")
    : (
        state?.item?.title ||
        state?.item?.name ||
        (slug ? slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "Area")
      );

  const city = embedded
    ? (propsCity || propsItem?.city || "Los Angeles")
    : (
        state?.city ||
        state?.item?.city ||
        state?.location ||
        state?.place ||
        state?.query ||
        "Los Angeles"
      );

  // Fallback image (in case Unsplash fails)
  const heroImage = embedded
    ? (propsHeroImage || propsItem?.image || localStorage.getItem("lastHeroImage") || "")
    : (
        state?.heroImage ||
        state?.image ||
        state?.item?.image ||
        localStorage.getItem("lastHeroImage") ||
        ""
      );

  useEffect(() => {
    if (heroImage) localStorage.setItem("lastHeroImage", heroImage);
  }, [heroImage]);

  // ui state
  const [category, setCategory] = useState(CATS[0]);
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 350);

  // data state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // build a stable cache key
  const cacheKey = useMemo(
    () => `poi:${areaTitle}|${city}|${category}|${dq}`,
    [areaTitle, city, category, dq]
  );

  useEffect(() => {
    let ignore = false;

    async function fetchPOI() {
      try {
        setLoading(true);
        setErr("");

        // 1) Get POIs (from local cache or API)
        const cached = localStorage.getItem(cacheKey);
        let baseItems;
        if (cached) {
          const parsed = JSON.parse(cached);
          baseItems = parsed.items || [];
        } else {
          const res = await fetch(`${API_BASE}/api/areas/poi`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({
              name: areaTitle,
              city,
              state: "CA",
              category,
              query: dq,
              limit: 12,
            }),
          });

          const text = await res.text();
          let json;
          try {
            json = JSON.parse(text);
          } catch {
            throw new Error(
              "Could not parse API response. Check API base URL / proxy. " +
                "Response starts with: " + text.slice(0, 60)
            );
          }

          if (!res.ok) throw new Error(json?.error || "poi fetch failed");
          localStorage.setItem(cacheKey, JSON.stringify(json));
          baseItems = json.items || [];
        }

        // 2) For each POI, build a Street View URL pointing at our backend
        // proxy. The actual HTTP fetch is done by the browser's <img> tag,
        // so we just assemble URLs here — no awaits, no Promise.all.
        // ResultCard's `image` is used as the primary src; if Street View
        // returns 404 (no imagery available), the <img> onError swaps in
        // `fallbackImage` (heroImage). Lat/lng are usually absent today
        // (POI schema doesn't include them) — the proxy will accept the
        // text query "{title}, {city}" and let Google geocode it.
        const enriched = (baseItems || []).map((it) => ({
          ...it,
          image: streetViewUrl(it.title, areaTitle, city, it.lat, it.lng, "400x250"),
          fallbackImage: heroImage || it.image || "",
        }));

        if (!ignore) setItems(enriched);
      } catch (e) {
        console.error(e);
        if (!ignore) {
          setErr(String(e.message || e));
          setItems([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    fetchPOI();
    return () => {
      ignore = true;
    };
  }, [API_BASE, cacheKey, areaTitle, city, heroImage]);

  // Round 6: embedded → AIChat owns the back button; standalone → keep topbar.
  const handleBack = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={`ex-wrap${embedded ? " ex-wrap-embedded" : ""}`}>
      {!embedded && (
        <div className="ex-topbar">
          <button className="ex-icon" onClick={handleBack} aria-label="Back">←</button>
        </div>
      )}

      {!embedded && (
        <div className="ex-head">
          <span className="ex-eyebrow">Nearby places</span>
          <h2 className="ex-title">
            Explore <span className="ex-title-accent">{areaTitle}</span>
          </h2>
          <button
            className="ex-bubble"
            onClick={() => navigate("/home")}
            title="Back to chat"
            aria-label="Back to chat"
          >
            💬
          </button>
        </div>
      )}

      {/* Embedded view: same eyebrow + headline pattern, slimmer header. */}
      {embedded && (
        <div className="ex-head ex-head-embedded">
          <span className="ex-eyebrow">Nearby places</span>
          <h2 className="ex-title">
            Explore <span className="ex-title-accent">{areaTitle}</span>
          </h2>
        </div>
      )}

      <div className="ex-search">
        <input
          className="ex-input"
          placeholder="Type"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {/* mic removed */}
      </div>

      <div className="ex-pills">
        {CATS.map((c) => (
          <button
            key={c}
            className={`ex-pill ${category === c ? "active" : ""}`}
            onClick={() => setCategory(c)}
          >
            {c}
          </button>
        ))}
      </div>

      {err && <div className="ex-err">{err}</div>}

      {loading ? (
        <div className="ex-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="ex-skel"
              style={{ height: 260, borderRadius: 14, animation: "pulse 1.5s ease-in-out infinite" }}
            />
          ))}
        </div>
      ) : items.length ? (
        <div className="ex-grid">
          {items.map((it, idx) => (
            <ResultCard
              key={idx}
              fullWidth
              item={{
                title: it.title,
                rating: it.rating ?? 5.0,
                reviews: it.reviews ?? "",
                subtitle: it.subtitle,
                image: it.image,
                // 2026-05-06 (Tier 2.3): if Street View has no imagery
                // for this POI (404 from /api/places/streetview),
                // fall back to the neighborhood hero shot rather than
                // the gray "No image" placeholder.
                fallbackImage: it.fallbackImage,
                ctaUrl: mapsUrl(it.title, city, it.lat, it.lng),
                ctaText: "Open in Maps →",
              }}
            />
          ))}
        </div>
      ) : (
        <div className="ex-empty">No results. Try a different search.</div>
      )}
      {/* Round 6 (2026-05-05): inline <style> block removed. The conflicting
          legacy rules used to override the proper ExploreMore.css; now that
          file is the single source of truth and uses splash design tokens. */}
    </div>
  );
}
